import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

interface DonutChartData {
  name: string;
  value: number;
  [key: string]: string | number;
}

interface DonutChartComponentProps {
  data: DonutChartData[];
  colors?: string[];
  height?: number;
  title?: string;
  centerText?: string;
  onSegmentClick?: (data: any, index: number) => void;
  selectedSentiment?: string | null;
}

const DEFAULT_COLORS = [
  '#3b82f6', // blue
  '#10b981', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // purple
  '#ec4899', // pink
];

const DonutChartComponent: React.FC<DonutChartComponentProps> = ({
  data,
  colors = DEFAULT_COLORS,
  height = 400,
  title,
  centerText,
  onSegmentClick,
  selectedSentiment,
}) => {
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  const handleClick = (data: any, index: number) => {
    if (onSegmentClick) {
      onSegmentClick(data, index);
    }
  };

  return (
    <div className="bg-panel rounded-lg shadow p-6">
      {title && (
        <h3 className="text-lg font-semibold text-heading mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, value, percent }: any) =>
              `${name} (${value}) ${(percent * 100).toFixed(0)}%`
            }
            outerRadius={120}
            innerRadius={70}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={2}
            onClick={handleClick}
            cursor={onSegmentClick ? 'pointer' : 'default'}
          >
            {data.map((entry, index) => {
              const isSelected = selectedSentiment && entry.name.toLowerCase() === selectedSentiment.toLowerCase();
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={isSelected ? (isDark ? '#e5e7eb' : '#000000') : colors[index % colors.length]}
                />
              );
            })}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgb(31 41 55)',
              border: 'none',
              borderRadius: '0.5rem',
              color: '#ffffff',
            }}
            itemStyle={{
              color: '#ffffff',
            }}
            labelStyle={{
              color: '#ffffff',
            }}
          />
          <Legend />
          {centerText && (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-2xl font-bold fill-gray-900 dark:fill-white"
            >
              {centerText}
            </text>
          )}
        </PieChart>
      </ResponsiveContainer>
      {!centerText && (
        <div className="text-center mt-4">
          <p className="text-2xl font-bold text-heading">
            {total}
          </p>
          <p className="text-sm text-muted">Total</p>
        </div>
      )}
    </div>
  );
};

export default DonutChartComponent;
